/**
 * Privacy signal detection for CCPA/CPRA compliance.
 * Detects GPC (Global Privacy Control) and DNT (Do Not Track) signals.
 */

const STORAGE_KEY = "zapido_tracking_opt_out";

export interface PrivacySignals {
  /** True if navigator.globalPrivacyControl is enabled */
  gpcEnabled: boolean;
  /** True if navigator.doNotTrack is "1" */
  dntEnabled: boolean;
  /** Combined signal - true if either GPC or DNT is enabled */
  shouldOptOut: boolean;
}

/**
 * Detect browser privacy signals (GPC and DNT).
 * GPC is the modern standard; DNT is legacy but still honored as fallback.
 */
export function detectPrivacySignals(): PrivacySignals {
  // Global Privacy Control - newer, more explicit standard

  const gpcEnabled = navigator.globalPrivacyControl === true;

  // Do Not Track - legacy but still honored
  const dntEnabled = navigator.doNotTrack === "1";

  return {
    gpcEnabled,
    dntEnabled,
    shouldOptOut: gpcEnabled || dntEnabled,
  };
}

/**
 * Get tracking opt-out preference from localStorage.
 * Used for non-authenticated visitors.
 */
export function getTrackingOptOutPreference(): boolean {
  try {
    const localPref = localStorage.getItem(STORAGE_KEY);
    return localPref === "true";
  } catch {
    // localStorage may not be available (private browsing, etc.)
    return false;
  }
}

/**
 * Set tracking opt-out preference in localStorage.
 * Used for non-authenticated visitors.
 */
export function setTrackingOptOutPreference(optOut: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, optOut ? "true" : "false");
  } catch {
    // localStorage may not be available
    console.warn("Unable to save tracking preference to localStorage");
  }
}

/**
 * Determine if advertising trackers should be disabled.
 * Returns true if any of: GPC enabled, DNT enabled, localStorage opt-out, or user database preference.
 *
 * @param userDbPreference - Optional user preference from database (for authenticated users)
 */
export function shouldDisableAdTracking(userDbPreference?: boolean): boolean {
  const signals = detectPrivacySignals();
  const localOptOut = getTrackingOptOutPreference();

  return signals.shouldOptOut || localOptOut || userDbPreference === true;
}
